import _findIndex from 'lodash.findindex';
import { Collider2d } from 'collider2d';
import { defineStore } from 'pinia';
import {
  LaserConfiguration,
  Personalization,
  PmsColor,
  ProductPrintArea,
} from '@midocean/webapps-shared-domain';
import { ProductPrintPanel } from '@/typings/ProductPrintData';
import {
  ArtworkPayload,
  LogoShape,
  TextShape,
  VariantArtworkImages,
} from '@/web-components/import-wizard/typings/pintura';
import { convertToBlob, mm2px, isPercentageValue } from '@/utils/common';
import { SmallDetailsPayload } from '@/typings';
import { useStore as useProductStore } from './product';
import { useStore as useActionsStore } from './action';
import { useStore as useHistoryStore } from './history';
import { useStore as useMirrorStore } from './mirror';
import customersAPI from '@/services/CustomersService';
import { DoSmallDetailsPart } from '@/constants/imageProcessingEngine';
import {
  AnnotationShape,
  PrintArea,
  Text,
} from '@/web-components/import-wizard-v2/typings/common';
import {
  getShapePositionInAreainPt,
  getShapePositionOnProductInMM,
} from '@/utils/areaHelper';
import { ProcessTextCommand, TextAlignment } from '@/typings/SmallDetails';
import FileService from '@/services/FileService';
import { base64StringToBlob } from 'blob-util';
import { positionText } from '@/utils/alignText';
import { createStateSnapshot } from '@/utils/historyHelper';
import { DefaultSmallDetailsResult } from '@/typings/SmallDetails';
import { useStore as useShapeBoxSectionStore } from '../shapeBoxSection';
import { useStore as useColorsManagementStore } from '@/stores/v2/colorsManagement';
import { useStore as useNotificationStore } from './notification';
import { alignAnnotationShape } from '@/utils/alignmentHelper';
import { lineWidth } from '@/constants/areaLineWidth';
import { createShapeFromPayload } from '@/utils/shapeHelper';
import {
  ChangeColorCommand,
  ChangeColorResult,
  RemoveColorCommand,
} from '@/web-components/import-wizard/typings';
import { startOfYesterday } from 'date-fns/esm';
import {
  determineNewWidthPx,
  determineNewHeightPx,
  determineNewWidthMm,
  determineNewHeightMm,
  px2mm,
} from '@/utils/common';
import {
  DoRoundCroppingCommand,
  SetArtworkBackgroundCommand,
} from '@/web-components/import-wizard/typings/colorManagement/colorManagement';
import { useStore as useColorMngStore } from './colorsManagement';

export const nonTextShapes = (shape: AnnotationShape): boolean =>
  shape.kind === 'Logo';
export const textShapes = (shape: AnnotationShape): boolean =>
  shape.kind === 'Text';

export const useStore = defineStore({
  id: 'shape',

  state: () => {
    return {
      activeShapeId: '',
      errors: [],
      collider2d: new Collider2d(),
      shapes: [] as AnnotationShape[],
      positionShapeMap: [] as Record<string, string>[],
      isLoaderActive: false,
      panelPosition: { x: 0, width: 0 } as ProductPrintPanel,
      personalization: {} as Personalization,
    };
  },
  getters: {
    positionShapes: (state) => {
      return (positionId: string) => {
        return state.shapes.filter((s) =>
          state.positionShapeMap.some(
            (psm) => psm.id === positionId && psm.logoId === s.id
          )
        );
      };
    },
  },
  actions: {
    async getPersonalization(id: string) {
      try {
        const { data } = await customersAPI.getPersonalization(id);
        this.personalization = data;
      } catch (e) {
        console.log(e);
      }
    },

    // TODO: Remove after implementing adding logo
    async addTempShapes() {
      const blob = await convertToBlob(
        'https://stcustdevwesteurope01.blob.core.windows.net/019886fd-67f2-4f24-bdb6-adb4c3b2a98a-personalizations/cbimage.png'
      );

      this.shapes.push({
        _isFormatted: false,
        _prerender: false,
        zindex: 0,
        kind: 'Logo',
        isRoundLogo: false,
        id: '10ded37f-338d-4ec8-8a8b-0cf82c7e6a26',
        backgroundImage: URL.createObjectURL(blob),
        pngFileWithoutConverters: blob,
        x: 0,
        y: 0,
        isInArea: true,
        width: 474.30876377952757,
        height: 100.08934934594869,
        aspectRatio: 4.738853503184713,
        xOnProductMM: 0,
        yOnProductMM: 0,
        widthOnProductMM: 0,
        heightOnProductMM: 0,
        smallDetailsResult: undefined,
        processed: '',
        isSelected: true,
        isEditing: true,
        rotation: 0,
        pngHorizontalResolutionDPIs: 541,
        pngVerticalResolutionDPIs: 541,
        fileName: 'file1.png',
        originalFileUrl:
          'https://stcustqawesteurope01.blob.core.windows.net/6f8f9ed9-b280-4cf0-b0a2-1957668a8bf5-personalizations/10ded37f-338d-4ec8-8a8b-0cf82c7e6a26-logo-original',
        pngFileUrl:
          'https://stcustdevwesteurope01.blob.core.windows.net/019886fd-67f2-4f24-bdb6-adb4c3b2a98a-personalizations/cbimage.png',
        pngWidthPixels: 1488,
        pngHeightPixels: 314,
        originalWidthMM: 393.7,
        originalHeightMM: 83.0791666666667,
        isVectorFileTrueVectorFile: false,
        spotColorsInOriginalFile: [],
        customerArtworkId: '00000000-0000-0000-0000-000000000000',
        personalizationArtworkId: '10ded37f-338d-4ec8-8a8b-0cf82c7e6a26',
        originalHorizontalResolutionDPIs: 96,
        originalVerticalResolutionDPIs: 96,
      } as unknown as AnnotationShape);

      this.positionShapeMap.push({
        logoId: '10ded37f-338d-4ec8-8a8b-0cf82c7e6a26',
        id: 'e1922fff-af79-4da2-69c8-08da3e357344',
      });
    },
    async addArtworkPayload(
      artworkPayload: ArtworkPayload,
      positionId: string
    ) {
      const productStore = useProductStore();
      const printableAreaRotation = productStore.rotation;
      const scaledArea = productStore.printPositionArea(positionId, true);
      const productArea = productStore.activePosition.printArea;

      if (scaledArea && productStore.activePosition && productArea) {
        const polygonDetails = productStore.polygonDetails(
          productStore.activePosition.id
        );
        const shapePayload = createShapeFromPayload(
          artworkPayload,
          scaledArea,
          printableAreaRotation,
          productStore.activePosition,
          polygonDetails
        );

        this.shapes.push(shapePayload);
        this.selectShape(shapePayload.id);
        this.positionShapeMap.push({
          id: positionId,
          logoId: shapePayload.id,
        });

        const colorManagementStore = useColorsManagementStore();
        if (
          shapePayload.kind === 'Logo' &&
          colorManagementStore.lastPalette !== 'ALL'
        ) {
          colorManagementStore.changeToClosestPantonesFromPalette(shapePayload);
        }
        this.updateShapePosition(shapePayload, scaledArea, productArea);

        if (productStore.selectedPrintPosition.techniqueId) {
          this.applyEffectIfNeeded(
            productStore.selectedPrintPosition.id,
            productStore.selectedPrintPosition.techniqueId,
            shapePayload
          );
        }
      }
    },
    async alignShape(shapeId: string, alignmentType: string) {
      const shape = this.shapes.find((s) => s.id === shapeId);
      const productStore = useProductStore();

      if (shape !== undefined && productStore.activePosition.id) {
        const { originalImageWebDpi, originalImageWidthPx, originalImageDpi } =
          productStore.activePosition;

        const webImageWidthPx =
          originalImageWidthPx &&
          originalImageWebDpi &&
          originalImageDpi &&
          (originalImageWidthPx * originalImageWebDpi) / originalImageDpi;
        const rectLineWidth = (lineWidth * webImageWidthPx) / 100;

        const { width, height } = shape;
        // TODO - check
        const actualWidth = isPercentageValue(shape.width)
          ? (shape.width / 100) *
            productStore.activePosition.originalImageWidthPx
          : width;
        const actualHeight = isPercentageValue(shape.height)
          ? (shape.height / 100) *
            productStore.activePosition.originalImageHeightPx
          : height;

        const printableAreaRotation = productStore.rotation;
        const scaledArea = productStore.printPositionArea(
          productStore.activePosition.id,
          true
        );
        const productArea = productStore.activePosition.printArea;
        const polygonDetails = productStore.polygonDetails(
          productStore.activePosition.id
        );

        if (scaledArea) {
          alignAnnotationShape(
            rectLineWidth,
            shape,
            scaledArea,
            printableAreaRotation,
            alignmentType,
            actualWidth,
            actualHeight,
            polygonDetails
          );
          this.updateShapePosition(shape, scaledArea, productArea);
        }
      }
    },
    async initializeStateFromPersonalization() {
      const productStore = useProductStore();
      const actionStore = useActionsStore();

      const printPositions =
        this.personalization.proofs[0].productPrintPositions;

      for (const pos of printPositions) {
        const product = productStore.data.productPrintPositions.find(
          (p) => p.name === pos.name
        );
        const positionID = product?.id;
        const originalDPI = product?.originalImageWebDpi as number;

        let mappedArtworkIds: Record<string, string>[] =
          pos.personalizationArtworks.map((a) => {
            return {
              id: positionID,
              logoId: a.id,
            } as Record<string, string>;
          });

        this.positionShapeMap = [...this.positionShapeMap, ...mappedArtworkIds];
        mappedArtworkIds = [];

        const isLaserConverter = productStore.isLaserConverter(
          positionID,
          pos.productPrintTechnique.sapCode
        );
        const isDebossing = productStore.isDebossingTechnique(
          positionID,
          pos.productPrintTechnique.sapCode
        );

        for (const a of pos.personalizationArtworks) {
          const imageBlob = await convertToBlob(a.pngFileUrl);

          let variantArtworkImages: VariantArtworkImages[] = [];

          if (isLaserConverter) {
            variantArtworkImages = await Promise.all(
              a.variantArtworkImages.map(async (vi) => {
                const imageBlob = await convertToBlob(vi.variantArtworkPngUrl);
                return {
                  variantColor: vi.variantColor,
                  variantArtworkPng: imageBlob,
                } as VariantArtworkImages;
              })
            );
          }

          let smallDetailsPayload: SmallDetailsPayload | undefined = undefined;

          if (DoSmallDetailsPart) {
            const isSmallDetailsTechnique =
              productStore.isSmallDetailsTechnique(
                positionID,
                pos.productPrintTechnique.sapCode
              );
            if (a.smallDetails) {
              smallDetailsPayload = {
                negativeDetailsLoss: a.smallDetails.negativeLoss,
                positiveDetailsLoss: a.smallDetails.positiveLoss,
                status: a.smallDetails.status,
                approvedAt: a.smallDetails.approvedAt
                  ? a.smallDetails.approvedAt.toString()
                  : undefined,
                isCheckValid: true,
                pngFile: a.smallDetails.fileUrl,
                pngFileBlob: await convertToBlob(a.smallDetails.fileUrl),
                calculatedQuality: a.smallDetails.calculatedQuality,
                id: a.id,
              };
            }

            if (isSmallDetailsTechnique && a.smallDetails === null) {
              smallDetailsPayload = {
                negativeDetailsLoss: 0,
                positiveDetailsLoss: 0,
                status: 'NoCheck',
                approvedAt: undefined,
                isCheckValid: true,
                pngFile: '',
                pngFileBlob: new Blob(),
                calculatedQuality: 0,
                id: a.id,
              };
            }
          }

          this.shapes.push({
            zindex: a.zIndex,
            isRoundLogo: a.isRoundLogo,
            id: a.id,
            pngFile: imageBlob,
            pngFileWithoutConverters: imageBlob,
            title: a.title !== null ? a.title : '',
            originalFileUrl: a.originalFileUrl,
            originalHorizontalResolutionDPIs:
              a.originalHorizontalResolutionDPIs,
            originalVerticalResolutionDPIs: a.originalVerticalResolutionDPIs,
            pngHorizontalResolutionDPIs: a.pngHorizontalResolutionDPIs,
            pngVerticalResolutionDPIs: a.pngVerticalResolutionDPIs,
            pngHeightPixels: a.pngHeightPixels,
            pngWidthPixels: a.pngWidthPixels,
            pmsColorsInPngFile: a.pmsColorsInPngFile as PmsColor[],
            personalizationArtworkId: a.id,
            customerId: '',
            fileName: a.fileName,
            originalHeightMM: a.originalHeightMM,
            originalWidthMM: a.originalWidthMM,
            thumbnailUrl: '',
            pngFileUrl: a.pngFileUrl,
            isVectorFileTrueVectorFile: a.isVectorFileTrueVectorFile,
            spotColorsInOriginalFile: [],
            customerArtworkId: '',
            processed: '',
            x: mm2px(a.distanceFromLeftWithCropping, originalDPI),
            y: mm2px(a.distanceFromTopWithCropping, originalDPI),
            width: mm2px(a.widthWithCropping, originalDPI),
            height: mm2px(a.heightWithCropping, originalDPI),
            rotation: a.rotationAngle * (Math.PI / 180),
            aspectRatio:
              mm2px(a.widthWithCropping, originalDPI) /
              mm2px(a.heightWithCropping, originalDPI),
            backgroundImage: URL.createObjectURL(
              variantArtworkImages.find(
                (v) => v.variantColor === productStore.selectedVariant.colour
              )?.variantArtworkPng || imageBlob
            ),
            smallDetailsResult: smallDetailsPayload,
            isInArea: false,
            isSelected: false,
            alignedTo: '',
            variantArtworkImages: variantArtworkImages,
            kind: 'Logo',
            widthOnProductMM: 0,
            heightOnProductMM: 0,
            xOnProductMM: 0,
            yOnProductMM: 0,
          } as AnnotationShape);

          actionStore.initializeUserActionForArtwork(a.id, a.userActions);
        }

        for (const t of pos.personalizationTexts) {
          const imageBlob =
            t.pngFileUrl !== null ? await convertToBlob(t.pngFileUrl) : '';
          const isLaserConverter = productStore.isLaserConverter(
            positionID,
            pos.productPrintTechnique.sapCode
          );

          let smallDetailsPayload: SmallDetailsPayload | undefined = undefined;

          if (DoSmallDetailsPart && t.smallDetails) {
            smallDetailsPayload = {
              negativeDetailsLoss: t.smallDetails.negativeLoss,
              positiveDetailsLoss: t.smallDetails.positiveLoss,
              status: t.smallDetails.status,
              approvedAt: t.smallDetails.approvedAt
                ? t.smallDetails.approvedAt.toString()
                : undefined,
              isCheckValid: true,
              pngFile: t.smallDetails.fileUrl,
              pngFileBlob: await convertToBlob(t.smallDetails.fileUrl),
              calculatedQuality: t.smallDetails.calculatedQuality,
              id: t.id,
            };
          }
          let colorArray = [];
          if (isLaserConverter) {
            const techniqueId = product?.printTechniques.find(
              (x) => x.printTechnique.code === pos.productPrintTechnique.sapCode
            )?.id;

            const laserColor = productStore.laserVariantColor(
              product?.id as string,
              techniqueId as string,
              productStore.selectedVariant.sku
            );
            const { red: r, green: g, blue: b } = laserColor.laserColor;
            colorArray = [r, g, b];
          } else {
            const { red: r, green: g, blue: b } = t.pmsColorInfo;
            colorArray = [r, g, b];
          }

          let variantArtworkImages: VariantArtworkImages[] = [];
          if (isLaserConverter || isDebossing) {
            variantArtworkImages = await Promise.all(
              t.variantArtworkImages.map(async (vi) => {
                const imageBlob = await convertToBlob(vi.variantArtworkPngUrl);
                return {
                  variantColor: vi.variantColor,
                  variantArtworkPng: imageBlob,
                } as VariantArtworkImages;
              })
            );
          }

          this.positionShapeMap = [
            ...this.positionShapeMap,
            { id: positionID, logoId: t.id } as Record<string, string>,
          ];

          const newShape = {
            zindex: t.zIndex,
            isRoundLogo: false,
            id: t.id,
            width: mm2px(t.width, originalDPI),
            height: mm2px(t.height, originalDPI),
            x: mm2px(t.distanceFromLeft, originalDPI),
            y: mm2px(t.distanceFromTop, originalDPI),
            rotation: t.rotationAngle * (Math.PI / 180),
            title: t.text,
            text: t.text,
            textHtml: t.textHtml,
            font: t.font,
            fontSizePT: t.fontSize,
            fontSize: t.fontSize * (originalDPI / 72),
            fontWeight: t.bold ? 'bold' : 'normal',
            fontStyle: t.italic ? 'italic' : 'normal',
            fontFamily: t.font,
            textAlign: t.textAlignement.toLowerCase(),
            colorName: t.pmsColorInfo.name,
            color: colorArray,
            disableInput: true,
            smallDetailsResult: smallDetailsPayload,
            isInArea: false,
            isSelected: false,
            kind: 'Text',
            backgroundImage:
              t.pngFileUrl !== null
                ? URL.createObjectURL(
                    variantArtworkImages.find(
                      (v) =>
                        v.variantColor === productStore.selectedVariant.colour
                    )?.variantArtworkPng || imageBlob
                  )
                : null,
            pngFile: imageBlob,
            pngFileWithoutConverters: imageBlob,
            variantArtworkImages: variantArtworkImages,
            originalWidthMM: t.originalHeightMM,
            originalHeightMM: t.originalWidthMM,
            disableResize: true,
            w2pText: {
              text: t.text,
              textHtml: t.textHtml,
              fontSizePT: Number(t.fontSize),
              fontSize: t.fontSize * (originalDPI / 72),
              fontWeight: t.bold ? 'bold' : 'normal',
              fontStyle: t.italic ? 'italic' : 'normal',
              fontFamily: t.font,
              textAlign: t.textAlignement.toLowerCase(),
              colorName: t.pmsColorInfo.name,
              color: colorArray,
              fontVariant: '',
            } as Text,
          } as TextShape;

          // generate the image for old personalizationtext
          this.shapes.push(newShape as AnnotationShape);
        }

        this.shapes.sort((a, b) => a.zindex - b.zindex);
      }
    },

    removeShape(id: string, createSnapshot: boolean) {
      const notificationStore = useNotificationStore();
      const historyStore = useHistoryStore();

      this.shapes = this.shapes.filter((s) => {
        return s.id !== id;
      });

      this.positionShapeMap = this.positionShapeMap.filter((psm) => {
        return psm.logoId !== id;
      });

      // would be nice to revalidate all instead of just deleting
      // notificationStore.allValidatorPipelineResults =
      //   notificationStore.allValidatorPipelineResults.filter((pr) => {
      //     return pr.payload.id !== id;
      //   });

      // notificationStore.notificationsForBell =
      //   notificationStore.notificationsForBell.filter((n) => {
      //     return n.payloadId !== id;
      //   });

      this.deselectShape();

      // notificationStore.revalidateEmptyPrintPositions();

      if (createSnapshot) {
        const snapshot = createStateSnapshot();
        historyStore.recordHistory(snapshot);
      }
    },

    deselectShape() {
      const shapeBoxSectionStore = useShapeBoxSectionStore();
      shapeBoxSectionStore.resetIsValidInput();
      const currentIndex = this.shapes.findIndex(
        (s) => s.id === this.activeShapeId
      );

      if (currentIndex) {
        this.$patch((state) => {
          const current = state.shapes[currentIndex];
          state.shapes[currentIndex] = {
            ...current,
            isSelected: false,
          };
          state.activeShapeId = '';
        });
      }
    },

    updateShapePosition(
      shape: AnnotationShape,
      scaledArea: PrintArea,
      productPrintArea: ProductPrintArea
    ) {
      const originalShapeIdx = this.shapes.findIndex((x) => x.id === shape.id);

      if (originalShapeIdx !== -1) {
        const newShapePosition = {
          x: shape.x,
          y: shape.y,
          width: shape.width,
          height: shape.height,
        };
        const positionOnAreaInPt = getShapePositionInAreainPt(
          newShapePosition,
          scaledArea
        );

        if (positionOnAreaInPt) {
          const positionOnProductInMM = getShapePositionOnProductInMM(
            positionOnAreaInPt,
            productPrintArea
          );

          if (positionOnProductInMM) {
            this.$patch((state) => {
              const current = state.shapes[originalShapeIdx];
              state.shapes.splice(originalShapeIdx, 1, {
                ...current,
                x: shape.x,
                y: shape.y,
                width: shape.width,
                height: shape.height,
                xOnProductMM: positionOnProductInMM.xOnProductMM,
                yOnProductMM: positionOnProductInMM.yOnProductMM,
                widthOnProductMM: positionOnProductInMM.widthOnProductMM,
                heightOnProductMM: positionOnProductInMM.heightOnProductMM,
              });
            });
          }
        }
      }
    },
    transformShape(key: string, value: number) {
      const mirrorStore = useMirrorStore();

      const currentShapeIndex = _findIndex(this.shapes, {
        id: this.activeShapeId,
      });

      const activeShape = this.shapes[currentShapeIndex];

      if (activeShape.kind === 'Logo' && key === 'width') {
        const newHeightMm = determineNewHeightMm(
          activeShape.width,
          activeShape.height,
          value
        );
        const newWidthPx = determineNewWidthPx(
          activeShape.width,
          activeShape.widthOnProductMM,
          value
        );
        const newHeightPx = determineNewHeightPx(
          activeShape.height,
          activeShape.heightOnProductMM,
          newHeightMm
        );

        const smallDetails = activeShape.smallDetailsResult
          ? { ...activeShape.smallDetailsResult, isCheckValid: false }
          : undefined;

        if (currentShapeIndex > -1) {
          this.$patch((state) => {
            state.shapes.splice(currentShapeIndex, 1, {
              ...state.shapes[currentShapeIndex],
              width: newWidthPx,
              height: newHeightPx,
              smallDetailsResult: smallDetails,
              widthOnProductMM: value,
              heightOnProductMM: newHeightMm,
            });
            // TODO - implement notifications
          });
        }
      }

      if (activeShape.kind === 'Logo' && key === 'height') {
        const newWidthMm = determineNewWidthMm(
          activeShape.width,
          activeShape.height,
          value
        );
        const newWidthPx = determineNewWidthPx(
          activeShape.width,
          activeShape.widthOnProductMM,
          newWidthMm
        );
        const newHeightPx = determineNewHeightPx(
          activeShape.height,
          activeShape.heightOnProductMM,
          value
        );

        const smallDetails = activeShape.smallDetailsResult
          ? { ...activeShape.smallDetailsResult, isCheckValid: false }
          : undefined;

        if (currentShapeIndex > -1) {
          this.$patch((state) => {
            state.shapes.splice(currentShapeIndex, 1, {
              ...state.shapes[currentShapeIndex],
              height: newHeightPx,
              width: newWidthPx,
              smallDetailsResult: smallDetails,
              widthOnProductMM: newWidthMm,
              heightOnProductMM: value,
            });
            // TODO - implement notifications
          });
        }
      }
      if (key === 'rotation') {
        const rotation = (value * Math.PI) / 180;
        this.$patch((state) => {
          state.shapes.splice(currentShapeIndex, 1, {
            ...state.shapes[currentShapeIndex],
            rotation,
          });
          mirrorStore.updateMirrorShape(this.shapes[currentShapeIndex]);
        });
      }

      // TODO - implement history
    },
    async makeLogoRound() {
      const productStore = useProductStore();
      const actionsStore = useActionsStore();

      const currentShapeIndex = _findIndex(this.shapes, {
        id: this.activeShapeId,
      });
      const activeShape = this.shapes[currentShapeIndex];

      const command: DoRoundCroppingCommand = {
        croppingAreaAspectRatioX: '1',
        croppingAreaAspectRatioY:
          (productStore.activePosition.printArea?.heightMm as number) /
          (productStore.activePosition.printArea?.widthMm as number),
      };
      try {
        this.isLoaderActive = true;

        if (
          activeShape.kind === 'Logo' &&
          productStore.activePosition.id &&
          productStore.selectedPrintPosition.techniqueId
        ) {
          const { data } = await FileService.roundCropping(
            activeShape.pngFileWithoutConverters as Blob,
            command
          );

          const affectedShape = this.shapes[currentShapeIndex];

          const smallDetailsResult = affectedShape.smallDetailsResult
            ? {
                ...affectedShape.smallDetailsResult,
                isCheckValid: false,
              }
            : undefined;

          //Calculate position and size that will fill area:
          const {
            originalImageWebDpi,
            originalImageWidthPx,
            originalImageDpi,
          } = productStore.activePosition;
          const webImageWidthPx =
            originalImageWidthPx &&
            originalImageWebDpi &&
            originalImageDpi &&
            (originalImageWidthPx * originalImageWebDpi) / originalImageDpi;
          const rectLineWidth = (lineWidth * (webImageWidthPx as number)) / 100;

          const scaledArea = productStore.printPositionArea(
            productStore.activePosition.id,
            true
          );
          const productArea = productStore.activePosition.printArea;

          if (scaledArea && scaledArea.kind === 'Ellipse') {
            const areaRect = {
              x: scaledArea.x - scaledArea.rx + rectLineWidth / 2,
              y: scaledArea.y - scaledArea.ry + rectLineWidth / 2,
              w: scaledArea.rx * 2 - rectLineWidth,
              h: scaledArea.ry * 2 - rectLineWidth,
            };

            let calcArtworkWidth = 0;
            let calcArtworkHeight = 0;
            const CORRECTION_FACTOR = 0;
            const artworkRatio = data.newWidth / data.newHeight;
            const areaRatio = areaRect.w / areaRect.h;
            // Artwork is more landscape than area - fit to width
            if (artworkRatio > areaRatio) {
              calcArtworkWidth = areaRect.w - CORRECTION_FACTOR;
              calcArtworkHeight =
                (calcArtworkWidth / data.newWidth) * data.newHeight;
            }
            // Artwork is more portrait than area - fit to height
            else {
              calcArtworkHeight = areaRect.h - CORRECTION_FACTOR;
              calcArtworkWidth =
                (calcArtworkHeight / data.newHeight) * data.newWidth;
            }

            //Calculate percentage croppend from each edge
            actionsStore.createCropAction(this.activeShapeId, {
              left:
                data.newWidth == activeShape.pngWidthPixels
                  ? 0
                  : ((activeShape.pngWidthPixels - data.newWidth / 2) /
                      activeShape.pngWidthPixels) *
                    100,
              right:
                data.newWidth == activeShape.pngWidthPixels
                  ? 0
                  : ((activeShape.pngWidthPixels - data.newWidth / 2) /
                      activeShape.pngWidthPixels) *
                    100,
              top:
                data.newHeight == activeShape.pngHeightPixels
                  ? 0
                  : ((activeShape.pngHeightPixels - data.newHeight / 2) /
                      activeShape.pngHeightPixels) *
                    100,
              bottom:
                data.newHeight == activeShape.pngHeightPixels
                  ? 0
                  : ((activeShape.pngHeightPixels - data.newHeight / 2) /
                      activeShape.pngHeightPixels) *
                    100,
            });

            activeShape.isRoundLogo = true;

            this.updateShapePosition(activeShape, scaledArea, productArea);

            this.$patch((state) => {
              state.shapes.splice(currentShapeIndex, 1, {
                ...activeShape,
                backgroundImage: URL.createObjectURL(
                  base64StringToBlob(data.pngFile, 'image/png')
                ),
                pngFile: base64StringToBlob(data.pngFile, 'image/png'),
                pngFileWithoutConverters: base64StringToBlob(
                  data.pngFile,
                  'image/png'
                ),
                pngHeightPixels: data.newHeight,
                pngWidthPixels: data.newWidth,
                width: calcArtworkWidth,
                height: calcArtworkHeight,
                aspectRatio: calcArtworkWidth / calcArtworkHeight,
                smallDetailsResult,
                x: areaRect.x + lineWidth,
                y: areaRect.y + lineWidth,
              });
            });

            await this.applyEffectIfNeeded(
              productStore.selectedPrintPosition.id,
              productStore.selectedPrintPosition.techniqueId,
              activeShape
            );

            const historyStore = useHistoryStore();
            const snapshot = createStateSnapshot();
            historyStore.recordHistory(snapshot);
          }
        }

        this.isLoaderActive = false;
      } catch (error) {
        this.isLoaderActive = false;
        console.log('An error occurred', error);
      }
    },
    async changeLogoBackground(proccessingType: string) {
      const actionsStore = useActionsStore();
      const command: SetArtworkBackgroundCommand = {
        isFullBackground: proccessingType === 'fullBackground',
        color: 2,
      };

      try {
        this.isLoaderActive = true;

        const activeShape = this.shapes.find(
          (x) => x.id === this.activeShapeId
        );
        if (activeShape && activeShape.kind === 'Logo') {
          const { data } = await FileService.changeLogoBackground(
            activeShape.pngFile,
            command
          );
          const whiteColor: PmsColor = {
            red: 255,
            green: 255,
            blue: 255,
            colorFuzz: 20,
            originalName: '',
            isOriginalSpotColor: false,
            name: 'Pure White',
            originalBlack: 0,
            originalCyan: 0,
            originalMagenta: 0,
            originalYellow: 0,
          };

          const artworkColors = activeShape.pmsColorsInPngFile;

          if (proccessingType === 'fullBackground') {
            actionsStore.createRemoveBackgroundFullAction(
              this.activeShapeId,
              whiteColor
            );
          }

          if (proccessingType !== 'fullBackground') {
            actionsStore.createRemoveBackgroundOutsideImageAction(
              this.activeShapeId,
              whiteColor
            );
          }

          if (data.isAllWhiteRemoved) {
            const whiteColorIndex = artworkColors.findIndex(
              (c) => c.name === whiteColor.name
            );
            if (whiteColorIndex !== -1) {
              artworkColors.splice(whiteColorIndex, 1);
            }
          }

          if (artworkColors.length !== 0) {
            this.updateArtworkBackground(
              data.pngFile,
              artworkColors,
              data.heightInPx,
              data.widthInPx
            );
          } else {
            await this.removeShape(activeShape.id, false);
          }

          // TODO - implement notifications and history
          // const notificationSore = useNotificationStore();
          // notificationSore.revalidateColorsOnly();

          // const historyStore = useHistoryStore();
          // const snapshot = createStateSnapshot();
          // historyStore.recordHistory(snapshot);
        }

        this.isLoaderActive = false;
      } catch (error) {
        this.isLoaderActive = false;
        console.log('An error occurred', error);
      }
    },
    async textToImage(
      font: string,
      fontSize: number,
      fontWeight: string,
      fontStyle: string,
      text: string,
      textAlignment: string,
      textColor: string,
      textHtml: string,
      color: PmsColor,
      repositionText: boolean,
      shapeId: string
    ) {
      try {
        this.isLoaderActive = true;

        const command: ProcessTextCommand = {
          font: font,
          fontSize: fontSize,
          id: shapeId,
          isBold: fontWeight === 'bold',
          isItalic: fontStyle === 'italic',
          text: text,
          textAlignment: textAlignment as TextAlignment,
          textColor: textColor,
        };

        const { data } = await FileService.textToImage(command);

        const textShapeIndex = _findIndex(this.shapes, {
          id: shapeId,
        });

        const productStore = useProductStore();

        this.$patch((state) => {
          const current = state.shapes[textShapeIndex];

          if (current.kind === 'Text') {
            state.shapes.splice(textShapeIndex, 1, {
              ...current,
              backgroundImage: URL.createObjectURL(
                base64StringToBlob(data.pngFile, 'image/png')
              ),
              pngFile: base64StringToBlob(data.pngFile, 'image/png'),
              pngFileWithoutConverters: base64StringToBlob(
                data.pngFile,
                'image/png'
              ),
              height: data.pngHeightPixels,
              width: data.pngWidthPixels,
              originalWidthMM: data.originalWidthMM,
              originalHeightMM: data.originalHeightMM,
              smallDetailsResult:
                current.smallDetailsResult !== undefined
                  ? {
                      ...current.smallDetailsResult,
                      isCheckValid: false,
                    }
                  : undefined,
              w2pText: {
                ...current.w2pText,
                fontFamily: font,
                fontSizePT: fontSize,
                fontWeight,
                fontStyle,
                textAlign: textAlignment,
                text: text,
                textHtml: textHtml,
                colorName: textColor,
                color: [color.red, color.green, color.blue],
              },
            });

            positionText(
              state.shapes[textShapeIndex] as TextShape,
              productStore.selectedPrintPosition.id,
              repositionText
            );

            if (productStore.selectedPrintPosition.techniqueId) {
              this.applyEffectIfNeeded(
                productStore.selectedPrintPosition.id,
                productStore.selectedPrintPosition.techniqueId,
                current
              );
            }

            const mirrorStore = useMirrorStore();
            mirrorStore.updateMirrorShape(state.shapes[textShapeIndex]);
          }
        });

        const historyStore = useHistoryStore();
        const snapshot = createStateSnapshot();
        historyStore.recordHistory(snapshot);

        this.isLoaderActive = false;
      } catch (error) {
        this.isLoaderActive = false;
        console.log('An error occurred', error);
      }
    },
    updateArtworkBackground(
      pngFile: string,
      artworkColors: PmsColor[],
      height: number,
      width: number
    ) {
      const index = this.shapes.findIndex(
        (shape: AnnotationShape) => shape.id === this.activeShapeId
      );

      const affectedShape = this.shapes[index];

      const aspectRatioWH =
        (affectedShape.width as number) / (affectedShape.height as number);
      const aspectRatioHW =
        (affectedShape.height as number) / (affectedShape.width as number);

      let recalculatedWidth = 0;
      let recalculatedHeight = 0;

      if (aspectRatioWH >= aspectRatioHW) {
        recalculatedHeight = (affectedShape.width as number) / (width / height);
        recalculatedWidth = affectedShape.width as number;
      } else {
        recalculatedHeight = affectedShape.height as number;
        recalculatedWidth = (affectedShape.height as number) / (height / width);
      }

      const smallDetailsResult = affectedShape.smallDetailsResult
        ? { ...affectedShape.smallDetailsResult, isCheckValid: false }
        : undefined;

      this.$patch((state) => {
        const current = state.shapes[index];
        if (current.kind === 'Logo') {
          state.shapes.splice(index, 1, {
            ...current,
            backgroundImage: URL.createObjectURL(
              base64StringToBlob(pngFile, 'image/png')
            ),
            pngFile: base64StringToBlob(pngFile, 'image/png'),
            pngFileWithoutConverters: base64StringToBlob(pngFile, 'image/png'),
            pmsColorsInPngFile: [...artworkColors],
            height: recalculatedHeight,
            width: recalculatedWidth,
            aspectRatio: recalculatedWidth / recalculatedHeight,
            smallDetailsResult,
            pngWidthPixels: width,
            pngHeightPixels: height,
          });
        }
      });

      // TODO - implement mirror
      //const mirrorStore = useMirrorStore();
      //mirrorStore.updateMirrorShape(this.shapes[index]);
    },
    async applyEffectIfNeeded(
      positionId: string,
      techniqueId: string,
      shapePayload: AnnotationShape
    ) {
      const productStore = useProductStore();

      const isLaserTechnique = productStore.isLaserConverter(positionId);

      const variantsArrayLength = shapePayload.variantArtworkImages
        ? shapePayload.variantArtworkImages.length
        : 0;

      if (isLaserTechnique && variantsArrayLength <= 0) {
        await this.applyLaserConverter(
          shapePayload.id,
          positionId,
          techniqueId
        );
      }
      if (productStore.isDebossingTechnique(positionId)) {
        await this.applyDebossingConverter(shapePayload.id);
      }
    },
    updateBackground(pngFile: string, shapePayload: AnnotationShape) {
      const shapeIndex = this.shapes.findIndex(
        (shape: AnnotationShape) => shape.id === shapePayload.id
      );
      const current = this.shapes[shapeIndex];
      if (current) {
        this.$patch((state) => {
          state.shapes.splice(shapeIndex, 1, {
            ...current,
            backgroundImage: pngFile,
          });
        });
      }
    },
    async applyLaserConverter(
      shapeId: string,
      positionId: string,
      techniqueId: string
    ) {
      try {
        this.isLoaderActive = true;
        const productStore = useProductStore();
        const mappedVariants =
          productStore.selectedVariants &&
          productStore.selectedVariants.map((el) => ({
            ...el,
            ...productStore.data.variants.find((v) => el.productName == v.name),
          }));

        const idx = this.shapes.findIndex((x) => x.id === shapeId);
        const shapePayload = { ...this.shapes[idx] };

        if (shapePayload) {
          shapePayload.variantArtworkImages = [];

          await Promise.all(
            mappedVariants.map(async (v) => {
              const laserConfiguration: LaserConfiguration =
                productStore.getVariantLaserColor(
                  positionId,
                  techniqueId,
                  v.sku as string
                );
              const laserEffectsCommand = {
                laserEffects: [
                  {
                    variantColor: v.variantColor,
                    laserPercentage: laserConfiguration.laserPercentage,
                    laserColor: laserConfiguration.laserColor,
                  },
                ],
              };
              if (laserConfiguration && shapePayload) {
                const { data } = await FileService.applyLaserEffect(
                  shapePayload.pngFileWithoutConverters as Blob,
                  JSON.stringify(laserEffectsCommand)
                );

                const variantImages = shapePayload.variantArtworkImages;
                this.$patch((state) => {
                  state.shapes.splice(idx, 1, {
                    ...state.shapes[idx],
                    ...{
                      variantArtworkImages: [
                        ...shapePayload.variantArtworkImages,
                        {
                          variantColor: v.colour as string,
                          variantArtworkPng: base64StringToBlob(
                            data.results[0].pngFile,
                            'image/png'
                          ),
                        },
                      ],
                    },

                    ...(productStore.selectedVariant.name === v.productName && {
                      pngFile: base64StringToBlob(
                        data.results[0].pngFile,
                        'image/png'
                      ),
                    }),
                  });
                });
                shapePayload.variantArtworkImages = [
                  ...variantImages,
                  {
                    variantColor: v.colour as string,
                    variantArtworkPng: base64StringToBlob(
                      data.results[0].pngFile,
                      'image/png'
                    ),
                  },
                ];
              }
            })
          );

          const actionsStore = useActionsStore();

          if (shapePayload && shapePayload.kind === 'Logo') {
            actionsStore.createLaserConverterAction(
              shapePayload.personalizationArtworkId
            );
          }

          this.isLoaderActive = false;
          const updatedShape = this.shapes[idx];
          const image = updatedShape.variantArtworkImages.find(
            (i) => i.variantColor === productStore.selectedVariant.colour
          )?.variantArtworkPng;

          if (image) {
            this.updateBackground(URL.createObjectURL(image), this.shapes[idx]);
          }
        }
      } catch (error) {
        this.isLoaderActive = false;
        console.log('An error occurred', error);
      }
    },
    async applyDebossingConverter(shapeId: string) {
      try {
        this.isLoaderActive = true;
        const productStore = useProductStore();
        const mappedVariants =
          productStore.selectedVariants &&
          productStore.selectedVariants.map((el) => ({
            ...el,
            ...productStore.data.variants.find((v) => el.productName == v.name),
          }));

        const idx = this.shapes.findIndex((x) => x.id === shapeId);
        const shapePayload = { ...this.shapes[idx] };

        if (shapePayload) {
          shapePayload.variantArtworkImages = [];

          await Promise.all(
            mappedVariants.map(async (v) => {
              if (shapePayload) {
                const variantImage = v.images?.find(
                  (i) =>
                    i.printPositionID == productStore.selectedPrintPosition.id
                );
                if (variantImage && variantImage.mostUsedColor) {
                  const { data } = await FileService.applyDebossingEffect(
                    shapePayload.pngFileWithoutConverters as Blob,
                    JSON.stringify({
                      debossingEffects: [
                        {
                          colorHex: variantImage.mostUsedColor,
                          variantColor: v.variantColor,
                        },
                      ],
                    })
                  );

                  const variantImages = shapePayload.variantArtworkImages;
                  this.$patch((state) => {
                    state.shapes.splice(idx, 1, {
                      ...state.shapes[idx],
                      ...{
                        variantArtworkImages: [
                          ...shapePayload.variantArtworkImages,
                          {
                            variantColor: v.colour as string,
                            variantArtworkPng: base64StringToBlob(
                              data.results[0].pngFile,
                              'image/png'
                            ),
                          },
                        ],
                      },

                      ...(productStore.selectedVariant.name ===
                        v.productName && {
                        pngFile: base64StringToBlob(
                          data.results[0].pngFile,
                          'image/png'
                        ),
                      }),
                    });
                  });
                  shapePayload.variantArtworkImages = [
                    ...variantImages,
                    {
                      variantColor: v.colour as string,
                      variantArtworkPng: base64StringToBlob(
                        data.results[0].pngFile,
                        'image/png'
                      ),
                    },
                  ];
                }
              }
            })
          );

          const actionsStore = useActionsStore();
          if (shapePayload) {
            if (shapePayload.kind === 'Logo') {
              actionsStore.createDebossingConverterAction(
                shapePayload.personalizationArtworkId
              );
            }

            this.isLoaderActive = false;
            const updatedShape = this.shapes[idx];
            const image = updatedShape.variantArtworkImages.find(
              (i) => i.variantColor === productStore.selectedVariant.colour
            )?.variantArtworkPng;

            if (image) {
              this.updateBackground(
                URL.createObjectURL(image),
                this.shapes[idx]
              );
            }
          }
        }
      } catch (error) {
        this.isLoaderActive = false;
        console.log('An error occurred', error);
      }
    },
    async addTextPayload(shapePayload: AnnotationShape, positionId: string) {
      if (shapePayload.kind === 'Text') {
        const productStore = useProductStore();

        shapePayload.w2pText.text = '';
        shapePayload.w2pText.textHtml = '';

        const positionId = productStore.selectedPrintPosition.id;
        const printPosition = productStore.data.productPrintPositions.find(
          (x) => x.id === positionId
        );
        const techniqueId = productStore.unavailablePrintPositions.find(
          (x) => x.positionId === positionId
        )?.technique.id;
        if (printPosition) {
          const limitation = printPosition.printTechniques.find(
            (x) => x.id === techniqueId
          )?.printTechnique.limitation;
          if (limitation) {
            shapePayload.smallDetailsResult = {
              ...DefaultSmallDetailsResult,
              isCheckValid: false,
            };
          }
          const colorManagementStore = useColorsManagementStore();
          if (colorManagementStore.lastPalette !== 'ALL') {
            shapePayload.w2pText.colorName =
              colorManagementStore.availablePmsColors[0].name;
            const { red, green, blue } =
              colorManagementStore.availablePmsColors[0];
            const colorRGBString = `rgba(${red},${green},${blue},1`;
            shapePayload.w2pText.color = [red, green, blue];
          }
        }
        this.shapes.push(shapePayload);
        this.selectShape(shapePayload.id);
        this.positionShapeMap.push({
          logoId: shapePayload.id,
          id: positionId,
        });
      }
    },
    selectShape(shapeId: string) {
      const shapeBoxSectionStore = useShapeBoxSectionStore();
      shapeBoxSectionStore.resetIsValidInput();
      this.activeShapeId = shapeId;
    },
    async replaceColor(from: PmsColor, to: PmsColor) {
      const productStore = useProductStore();
      const actionsStore = useActionsStore();
      const mirrorStore = useMirrorStore();

      const textPrintPositionShapes = this.shapes
        .filter((shape) =>
          this.positionShapeMap.some(
            (psm) =>
              psm.id === productStore.selectedPrintPosition.id &&
              psm.logoId === shape.id
          )
        )
        .filter(textShapes)
        .filter((x) => x.kind === 'Text' && x.w2pText.colorName === from.name);

      textPrintPositionShapes.map((t) => {
        const textShapeIndex = _findIndex(this.shapes, {
          id: t.id,
        });

        this.$patch((state) => {
          const current = state.shapes[textShapeIndex];
          if (current.kind === 'Text') {
            this.textToImage(
              current.w2pText.fontFamily,
              current.w2pText.fontSizePT,
              current.w2pText.fontWeight,
              current.w2pText.fontStyle,
              current.w2pText.text,
              current.w2pText.textAlign,
              to.name,
              current.w2pText.textHtml,
              to,
              false,
              current.id
            );
          }
        });

        mirrorStore.updateMirrorShape(this.shapes[textShapeIndex]);
      });

      const nonTextPrintPositionShapes = this.shapes
        .filter((shape) =>
          this.positionShapeMap.some(
            (psm) =>
              psm.id === productStore.selectedPrintPosition.id &&
              psm.logoId === shape.id
          )
        )
        .filter(nonTextShapes) as LogoShape[];

      const artworksForProcessing = nonTextPrintPositionShapes.filter((a) => {
        return a.pmsColorsInPngFile.some((c) => c.name.includes(from.name));
      });

      if (artworksForProcessing.length > 0) {
        for (const artwork of artworksForProcessing) {
          const channel = artwork.pmsColorsInPngFile;
          const colorsToChange =
            artwork.pmsColorsInPngFile.filter((c) => c.name === from.name) ||
            from;
          const lastBlob = artwork.pngFile as Blob;
          const mappedPngFile = {
            id: artwork.personalizationArtworkId,
            file: artwork.pngFile as Blob,
          };
          for (let i = 0; i < colorsToChange.length; i++) {
            const command: ChangeColorCommand = {
              oldColorToReplace: colorsToChange[i],
              newColorToSet: to,
              channels: channel,
            };

            try {
              this.isLoaderActive = true;

              const { data } = await FileService.replaceColor(command, [
                lastBlob,
              ]);

              actionsStore.createReplaceColorAction(mappedPngFile.id, from, to);

              this.isLoaderActive = false;

              const artworkPmsColors = [...artwork.pmsColorsInPngFile];
              to.colorFuzz = 1;

              const artworkPmsColorsWithSameName = artworkPmsColors.filter(
                (x) => x.name.includes(from.name)
              );

              artworkPmsColorsWithSameName.forEach((artworkPmsColor) => {
                artworkPmsColor.name = to.name;
                artworkPmsColor.originalName = to.name;
                artworkPmsColor.red = to.red;
                artworkPmsColor.green = to.green;
                artworkPmsColor.blue = to.blue;
                artworkPmsColor.isOriginalSpotColor = from.isOriginalSpotColor;
                artworkPmsColor.colorFuzz = to.colorFuzz;
              });

              const logoShapeIndex = _findIndex(this.shapes, {
                id: artwork.id,
              });

              this.$patch((state) => {
                const originalShape = state.shapes[logoShapeIndex];
                if (originalShape.kind === 'Logo') {
                  state.shapes.splice(logoShapeIndex, 1, {
                    ...originalShape,
                    pngFile: base64StringToBlob(data.pngFiles[0], 'image/png'),
                    pngFileWithoutConverters: base64StringToBlob(
                      data.pngFiles[0],
                      'image/png'
                    ),
                    pmsColorsInPngFile: artworkPmsColors,
                    backgroundImage: URL.createObjectURL(
                      base64StringToBlob(data.pngFiles[0], 'image/png')
                    ),
                  });
                  mirrorStore.updateMirrorShape(this.shapes[logoShapeIndex]);
                }
              });
            } catch (error) {
              console.log('An error occurred', error);
              this.isLoaderActive = false;
            }
          }
        }
      }
    },
    async removeColor(color: PmsColor) {
      const productStore = useProductStore();
      const actionsStore = useActionsStore();
      const colorMngStore = useColorsManagementStore();
      const mirrorStore = useMirrorStore();

      const textPrintPositionShapes = this.shapes
        .filter((shape) =>
          this.positionShapeMap.some(
            (psm) =>
              psm.id === productStore.selectedPrintPosition.id &&
              psm.logoId === shape.id
          )
        )
        .filter(textShapes)
        .filter((t) => t.kind === 'Text' && t.w2pText.colorName === color.name);

      textPrintPositionShapes.map((t) => {
        const textShapeIndex = _findIndex(this.shapes, {
          id: t.id,
        });

        if (textShapeIndex !== -1) {
          this.removeShape(t.id, false);
        }
      });

      const nonTextPrintPositionShapes = this.shapes
        .filter((shape) =>
          this.positionShapeMap.some(
            (psm) =>
              psm.id === productStore.selectedPrintPosition.id &&
              psm.logoId === shape.id
          )
        )
        .filter(nonTextShapes) as LogoShape[];

      const artworksForProcessing = nonTextPrintPositionShapes.filter((a) => {
        return a.pmsColorsInPngFile.some((c) => c.name.includes(color.name));
      });

      if (artworksForProcessing.length > 0) {
        const colorsWithSameName = new Array<PmsColor>();
        artworksForProcessing.forEach((artwork) => {
          artwork.pmsColorsInPngFile.forEach((pmsColor) => {
            if (pmsColor.name == color.name) {
              colorsWithSameName.push(pmsColor);
            }
          });
        });
        const mapedPngFiles = artworksForProcessing.map((a) => {
          return { id: a.personalizationArtworkId, file: a.pngFile as Blob };
        });
        let latestBlobs = mapedPngFiles.map((x) => {
          return x.file;
        });
        let latestData: ChangeColorResult = { pngFiles: new Array<string>() };
        for (let i = 0; i < colorsWithSameName.length; i++) {
          const command: RemoveColorCommand = {
            colorToRemove: colorsWithSameName[i],
            channels: colorMngStore.getPmsColorsOnPrintPosition(),
          };
          try {
            this.isLoaderActive = true;

            const { data } = await FileService.removeColor(
              command,
              latestBlobs
            );
            latestBlobs = data.pngFiles.map((x) => {
              return base64StringToBlob(x);
            });
            latestData = data;
          } catch (error) {
            this.isLoaderActive = false;
            console.log('An error occurred', error);
          }
        }
        mapedPngFiles.map((x, i) => {
          actionsStore.createRemoveColorAction(x.id, color);

          const shapeIndex = _findIndex(this.shapes, {
            id: x.id,
          });

          const originalShape = this.shapes[shapeIndex];
          if (originalShape && originalShape.kind === 'Logo') {
            const shapePmsColors = originalShape.pmsColorsInPngFile;

            if (shapePmsColors.length === 1) {
              // NOTE: Notifications for bell and all validator pipeline results are left hanging even though nothing exists on Pintura

              // If it's the last colour then the artwork is removed
              this.removeShape(x.id, false);
            } else {
              if (shapePmsColors) {
                const newPmsColors = shapePmsColors.filter(
                  (x) => !x.name.includes(color.name)
                );

                const smallDetails = this.shapes[shapeIndex].smallDetailsResult;

                this.$patch((state) => {
                  state.shapes.splice(shapeIndex, 1, {
                    ...originalShape,
                    pngFile: base64StringToBlob(
                      latestData.pngFiles[i],
                      'image/png'
                    ),
                    backgroundImage: URL.createObjectURL(
                      base64StringToBlob(latestData.pngFiles[i], 'image/png')
                    ),
                    pngFileWithoutConverters: base64StringToBlob(
                      latestData.pngFiles[i],
                      'image/png'
                    ),
                    pmsColorsInPngFile: newPmsColors,
                    smallDetailsResult: smallDetails
                      ? { ...smallDetails, isCheckValid: false }
                      : undefined,
                  });
                });

                mirrorStore.updateMirrorShape(this.shapes[shapeIndex]);
              }
            }
          }
        });
        this.isLoaderActive = false;
      }
    },
  },
});
